import React, { ForwardedRef, forwardRef, useContext, useEffect, useState } from 'react';
import { formatFilterPrice, Icon, IconEnum } from '@investoro/core';
import styled from 'styled-components';
import { purchaseApiRequests } from 'utils/api-requests/purchase';
import { userApiRequests } from 'utils/api-requests/user';
import { SubscriptionPurchaseContext } from 'utils/context/SubscriptionPurchaseContext';
import { OrderListDto, OrderListDtoOrderStatusEnum } from 'utils/types/OrderModels';
import { v4 } from 'uuid';

import Indicator from 'components/atom/Indicator';

import { Title } from './Title';

interface InvoicesProps {
    title: string;
}

const InvoicesContainer = styled.div`
    padding: 32px 31px;
    border-radius: var(--box-border-radius);
    background: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 60px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
`;

const EmptyContainer = styled.div`
    text-align: center;
    padding: 40px;
    color: var(--color-text-secondary);
`;

const TableScrollContainer = styled.div`
    @media (max-width: 768px) {
        width: 100%;
        overflow-x: auto;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th`
    padding: 15px 10px;
    text-align: left;
    font-weight: bold;
    border-bottom: 1px solid var(--color-alt-second);
    color: var(--color-primary);
`;

const TableRow = styled.tr<{ isGray: boolean }>`
    background-color: ${(props) => props.isGray ? 'var(--color-alt-second)' : 'var(--color-white)'};
    transition: background-color 0.2s ease;
    
    td:first-child {
        border-top-left-radius: var(--image-border-radius);
        border-bottom-left-radius: var(--image-border-radius);
    }

    td:last-child {
        border-top-right-radius: var(--image-border-radius);
        border-bottom-right-radius: var(--image-border-radius);
    }
`;

const TableCell = styled.td`
    padding: 15px 10px;
    vertical-align: top;
`;

const OrderNumber = styled.div`
    font-weight: 600;
    color: var(--color-primary);
    font-size: 14px;
`;

const ProductsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ProductItem = styled.div`
    font-size: 13px;
    color: var(--color-text-secondary);
`;

const StatusBadge = styled.span<{ status: OrderListDtoOrderStatusEnum }>`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    background-color: ${(props) => {
        switch (props.status) {
        case OrderListDtoOrderStatusEnum.COMPLETED:
        case OrderListDtoOrderStatusEnum.PAID:
            return 'var(--color-success-background)';
        case OrderListDtoOrderStatusEnum.PENDING:
        case OrderListDtoOrderStatusEnum.REGISTERED:
            return 'var(--color-success-warning)';
        case OrderListDtoOrderStatusEnum.FAILED:
        case OrderListDtoOrderStatusEnum.CANCELLED:
        case OrderListDtoOrderStatusEnum.EXPIRED:
            return 'var(--color-success-error)';
        default:
            return 'var(--color-alt-second)';
        }
    }};
    color: ${(props) => {
        switch (props.status) {
        case OrderListDtoOrderStatusEnum.COMPLETED:
        case OrderListDtoOrderStatusEnum.PAID:
            return 'var(--color-success)';
        case OrderListDtoOrderStatusEnum.PENDING:
        case OrderListDtoOrderStatusEnum.REGISTERED:
            return 'var(--color-warning)';
        case OrderListDtoOrderStatusEnum.FAILED:
        case OrderListDtoOrderStatusEnum.CANCELLED:
        case OrderListDtoOrderStatusEnum.EXPIRED:
            return 'var(--color-error)';
        default:
            return 'var(--color-text-secondary)';
        }
    }};
`;

const StatusContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RefreshButton = styled.div`
    padding: 4px 8px;
    font-size: 11px;
    min-height: auto;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;

    img {
        height: 16px;
        width: 16px;
    }
`;

const PriceCell = styled.div`
    font-weight: 600;
    color: var(--color-primary);
`;

const getStatusText = (status: OrderListDtoOrderStatusEnum): string => {
    switch (status) {
    case OrderListDtoOrderStatusEnum.COMPLETED:
        return 'Zrealizowane';
    case OrderListDtoOrderStatusEnum.PAID:
        return 'Opłacone';
    case OrderListDtoOrderStatusEnum.PENDING:
        return 'Oczekujące';
    case OrderListDtoOrderStatusEnum.REGISTERED:
        return 'Zarejestrowane';
    case OrderListDtoOrderStatusEnum.FAILED:
        return 'Nieudane';
    case OrderListDtoOrderStatusEnum.CANCELLED:
        return 'Anulowane';
    case OrderListDtoOrderStatusEnum.EXPIRED:
        return 'Wygasłe';
    default:
        return 'Nieznany';
    }
};

const Invoices = forwardRef<HTMLDivElement, InvoicesProps>(({ title }, ref: ForwardedRef<HTMLDivElement>) => {
    const [orders, setOrders] = useState<OrderListDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { handleCheckPaymentStatus } = useContext(SubscriptionPurchaseContext);

    const shouldShowRefreshButton = (order: OrderListDto): boolean => {
        const hasSubscriptionProduct = order.orderItems.some((item) => {
            const productName = item.productName.toLowerCase();

            return productName.includes('subskrypcja') ||
               productName.includes('subscription');
        });

        const isPendingOrRegistered = [
            OrderListDtoOrderStatusEnum.PENDING,
            OrderListDtoOrderStatusEnum.REGISTERED
        ].includes(order.orderStatus);

        return hasSubscriptionProduct && isPendingOrRegistered;
    };

    useEffect(() => {
        setIsLoading(true);

        purchaseApiRequests.getOrderList()
            .then((ordersList) => {
                setOrders(ordersList);
            })
            .catch((error) => {
                const errorId = v4();
                userApiRequests.logException(error as Error, errorId);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    if (isLoading) {
        return (
            <div ref={ref}>
                <Title>{title}</Title>
                <InvoicesContainer>
                    <LoadingContainer>
                        <Indicator />
                    </LoadingContainer>
                </InvoicesContainer>
            </div>
        );
    }

    if (orders.length === 0) {
        return (
            <div ref={ref}>
                <Title>{title}</Title>
                <InvoicesContainer>
                    <EmptyContainer>
                        <p>Nie masz jeszcze żadnych zamówień.</p>
                    </EmptyContainer>
                </InvoicesContainer>
            </div>
        );
    }

    return (
        <div ref={ref}>
            <Title>{title}</Title>
            <InvoicesContainer>
                <TableScrollContainer>
                    <Table>
                        <thead>
                            <tr>
                                <TableHeader>Numer zamówienia</TableHeader>
                                <TableHeader>Produkty</TableHeader>
                                <TableHeader>Status</TableHeader>
                                <TableHeader>Łącznie</TableHeader>
                                <TableHeader></TableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order, index) => <TableRow key={order.orderNumber} isGray={index % 2 === 0}>
                                <TableCell>
                                    <OrderNumber>
                                        #{order.orderNumber.slice(-8)}
                                    </OrderNumber>
                                </TableCell>
                                <TableCell>
                                    <ProductsList>
                                        {order.orderItems.map((item, itemIndex) => <ProductItem key={itemIndex}>
                                            {item.productName} × {item.quantity}
                                        </ProductItem>)}
                                    </ProductsList>
                                </TableCell>
                                <TableCell>
                                    <StatusContainer>
                                        <StatusBadge status={order.orderStatus}>
                                            {getStatusText(order.orderStatus)}
                                        </StatusBadge>
                                        {shouldShowRefreshButton(order) &&
                                    <RefreshButton onClick={() => handleCheckPaymentStatus()}>
                                        odśwież <Icon icon={IconEnum.RELOAD} />
                                    </RefreshButton>
                                        }
                                    </StatusContainer>
                                </TableCell>

                                <TableCell>
                                    <PriceCell>
                                        {formatFilterPrice(order.totalPrice / 100)} zł
                                    </PriceCell>
                                </TableCell>
                            </TableRow>)}
                        </tbody>
                    </Table>
                </TableScrollContainer>
            </InvoicesContainer>
        </div>
    );
});

Invoices.displayName = 'Invoices';

export default Invoices;
